import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toApiError, useApi, type UserResponse } from '@/shared/api';
import { userKeys } from '../queries';
import {
  planBulkAction,
  runBulkAction,
  type BulkActionId,
  type BulkOutcome,
} from './bulkUserActions';

/**
 * Führt eine Sammelaktion über die vorhandenen Einzelendpunkte aus.
 *
 * <p>Die Liste wird genau einmal am Ende neu geladen, nicht nach jedem Aufruf — sonst wackelt
 * die Tabelle während des Laufs unter der Auswahl weg.</p>
 */
export function useBulkUserActions(currentUserId?: string) {
  const { t } = useTranslation('accountRetention');
  const api = useApi();
  const queryClient = useQueryClient();
  const [running, setRunning] = useState<BulkActionId | null>(null);
  const [result, setResult] = useState<{ action: BulkActionId; outcome: BulkOutcome } | null>(null);

  const call = (action: BulkActionId, user: UserResponse) => {
    if (action === 'deactivate') return api.users.deactivate(user.id);
    if (action === 'reactivate') return api.users.reactivate(user.id);
    return api.users.resendInvitation(user.id);
  };

  const run = async (action: BulkActionId, selected: UserResponse[]) => {
    setRunning(action);
    try {
      const plan = planBulkAction(action, selected, currentUserId);
      const outcome = await runBulkAction(
        plan,
        (user) => call(action, user),
        async (error) => {
          const problem = await toApiError(error);
          return problem.title === 'User.RetentionExpired'
            ? t('expired')
            : (problem.detail ?? problem.title);
        },
      );
      await queryClient.invalidateQueries({ queryKey: userKeys.all });
      setResult({ action, outcome });
      return outcome;
    } finally {
      setRunning(null);
    }
  };

  return { running, result, run, dismissResult: () => setResult(null) };
}
